import { fetcher } from '@commenda/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keyPersonsKeys } from '../key-persons/keys';
import { personsInEntityKeys } from '../persons/usePersonsInEntity';
import { stockClassKeys } from '../stock-class/keys';
import { toOwnershipApiError } from './errors';
import { ownershipKeys } from './keys';
import {
  directorDeletePreviewSchema,
  inheritableUboListSchema,
  ownershipResponseSchema,
} from './responseSchemas';
import {
  CreateBeneficialOwnerRequest,
  CreateBeneficialOwnerResult,
  CreateShareholdingRequest,
  CreateShareholdingResult,
  DeleteAllBeneficialOwnersResult,
  DirectorDeletePreview,
  InheritableUltimateBeneficialOwner,
  OwnershipResponse,
  UpdateBeneficialOwnerRequest,
  UpdateShareClassTotalSharesRequest,
  UpdateShareholdingRequest,
  UpdateShareholdingResult,
} from './types';

async function ownershipFetch<T = void>(
  url: string,
  init: { method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT'; body?: object },
  fallbackMessage: string,
): Promise<T> {
  const response = await fetcher(url, init);
  if (!response.ok) {
    throw await toOwnershipApiError(response, fallbackMessage);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// Ownership writes upsert CHD rows on the BE, so the persons-in-entity
// cache for this corp must invalidate alongside the ownership cache. They
// can also create a StockClass atomically (via `newStockClass`), so the
// stock-class picker's cache invalidates here too.
function useOwnershipMutation<TVars, TData>(
  corporationId: number,
  mutationFn: (vars: TVars) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ownershipKeys.all });
      queryClient.invalidateQueries({ queryKey: stockClassKeys.all });
      // Declaring a UBO writes the same role the key-persons surface reads.
      queryClient.invalidateQueries({ queryKey: keyPersonsKeys.all });
      queryClient.invalidateQueries({
        queryKey: [
          personsInEntityKeys.FETCH_PERSONS_IN_ENTITY,
          { businessEntityId: corporationId },
        ],
      });
    },
  });
}

// Wraps the corp-id passing + cache-invalidation boilerplate so each hook
// only has to express its URL, method, body, and error message.
function defineOwnershipMutation<TVars, TData>(
  fetchFn: (corporationId: number, vars: TVars) => Promise<TData>,
) {
  return (corporationId: number) =>
    useOwnershipMutation(corporationId, (vars: TVars) => fetchFn(corporationId, vars));
}

export type UseOwnershipParams = { offset?: number; limit?: number };

export const useOwnership = (
  corporationId: number | null | undefined,
  params?: UseOwnershipParams,
) =>
  useQuery({
    queryKey: ownershipKeys.FETCH_OWNERSHIP(corporationId as number, params?.offset, params?.limit),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params?.offset !== undefined) qs.append('offset', String(params.offset));
      if (params?.limit !== undefined) qs.append('limit', String(params.limit));
      const queryString = qs.toString();
      const url = `ownership/${corporationId as number}${queryString ? `?${queryString}` : ''}`;
      const response = await fetcher(url, { method: 'GET' });
      if (!response.ok) throw new Error('Failed to fetch ownership');
      return ownershipResponseSchema.parse(await response.json()) as OwnershipResponse;
    },
    enabled: !!corporationId && corporationId > 0,
  });

export const useInheritableUltimateBeneficialOwners = (
  corporationId: number | null | undefined,
  sourceCorporationId: number | null | undefined,
) =>
  useQuery({
    queryKey: ownershipKeys.FETCH_INHERITABLE_UBOS(corporationId as number, sourceCorporationId as number),
    queryFn: async () => {
      // BE query-string name kept for back-compat.
      const url = `ownership/${corporationId as number}/inheritable-ubos?entityShareholderId=${sourceCorporationId as number}`;
      const response = await fetcher(url, { method: 'GET' });
      if (!response.ok) throw new Error('Failed to fetch inheritable UBOs');
      return inheritableUboListSchema.parse(await response.json()) as InheritableUltimateBeneficialOwner[];
    },
    enabled: !!corporationId && !!sourceCorporationId,
  });

export const useDirectorDeletePreview = (directorId: number | null | undefined) =>
  useQuery({
    queryKey: ownershipKeys.FETCH_DIRECTOR_DELETE_PREVIEW(directorId as number),
    queryFn: async () => {
      const response = await fetcher(
        `ownership/directors/${directorId as number}/delete-preview`,
        { method: 'GET' },
      );
      if (!response.ok) throw new Error('Failed to fetch delete preview');
      return directorDeletePreviewSchema.parse(await response.json()) as DirectorDeletePreview;
    },
    enabled: !!directorId,
  });

// Single create endpoint takes a discriminated envelope. Caller wraps the
// branch-specific payload via `{ type: 'INDIVIDUAL', individual: {...} }`
// or `{ type: 'INSTITUTION', institution: {...} }`.
export const useCreateShareholding = defineOwnershipMutation((corp, body: CreateShareholdingRequest) =>
  ownershipFetch<CreateShareholdingResult>(
    `ownership/${corp}/shareholdings`,
    { method: 'POST', body },
    'Failed to create shareholding',
  ),
);

// Single update endpoint addressed by stakeholdingId. Body uses the same
// discriminated envelope shape as create.
export const useUpdateShareholding = defineOwnershipMutation(
  (corp, { stakeholdingId, body }: { stakeholdingId: string; body: UpdateShareholdingRequest }) =>
    ownershipFetch<UpdateShareholdingResult>(
      `ownership/${corp}/shareholdings/${stakeholdingId}`,
      { method: 'PATCH', body },
      'Failed to update shareholding',
    ),
);

export const useDeleteShareholding = defineOwnershipMutation((corp, stakeholdingId: string) =>
  ownershipFetch<void>(
    `ownership/${corp}/shareholdings/${stakeholdingId}`,
    { method: 'DELETE' },
    'Failed to delete shareholding',
  ),
);

export const useCreateBeneficialOwner = defineOwnershipMutation((corp, body: CreateBeneficialOwnerRequest) =>
  ownershipFetch<CreateBeneficialOwnerResult>(`ownership/${corp}/beneficial-owners`, { method: 'POST', body }, 'Failed to create beneficial owner'),
);

// Director-scoped (no corp) so it can't go through useOwnershipMutation —
// only invalidates the ownership cache, no corp-specific persons cache.
export const useDeleteAllBeneficialOwnersForDirector = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (directorId: number) =>
      ownershipFetch<DeleteAllBeneficialOwnersResult>(
        `ownership/beneficial-owners/director/${directorId}`,
        { method: 'DELETE' },
        'Failed to remove beneficial owner records',
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ownershipKeys.all }),
  });
};

export const useUpdateBeneficialOwner = defineOwnershipMutation(
  (corp, { directorId, body }: { directorId: number; body: UpdateBeneficialOwnerRequest }) =>
    ownershipFetch<void>(`ownership/${corp}/beneficial-owners/${directorId}`, { method: 'PATCH', body }, 'Failed to update beneficial owner'),
);

export const useDeleteShareClass = defineOwnershipMutation((corp, shareClassId: string) =>
  ownershipFetch<{ id: string }>(
    `ownership/${corp}/share-classes/${shareClassId}`,
    { method: 'DELETE' },
    'Failed to delete share class',
  ),
);

// No batch endpoint for share classes — fires one PATCH per changed row.
// If any row fails the whole mutation rejects (no partial invalidation),
// matching how the caller stops the user editing mid-request.
export const useUpdateShareClassTotals = defineOwnershipMutation(
  (corp, updates: UpdateShareClassTotalSharesRequest[]) =>
    Promise.all(
      updates.map(({ shareClassId, authorizedShares }) =>
        ownershipFetch<{ id: string }>(
          `ownership/${corp}/share-classes/${shareClassId}`,
          {
            method: 'PATCH',
            body: { additionalInformation: { authorizedShares, authorizedSharesType: 'Custom' } },
          },
          'Failed to update share class',
        ),
      ),
    ),
);
